import React, { useEffect, useState } from 'react';
import { Box, Button, GlobalStyles, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  KB_HOME_PATH,
  MENU_ITEMS,
  SITE_TAGLINE,
  SITE_TITLE,
  asset,
  kbUrl
} from '../pages/public/epublisher-kb/model';

export { KB_HOME_PATH };

// Design tokens taken from the original ePublisher Knowledge Base theme
export const KB_COLORS = {
  accent: '#45BF96',
  accentDark: '#2FA27A',
  heading: '#232429',
  text: '#575863',
  muted: '#8A949B',
  pageBg: '#E5E9F0',
  line: '#E5E9F0',
  footerBg: '#232429'
};

const FONT_STACK = '"Roboto KB", Roboto, Arial, sans-serif';
const SUGGESTED = ['Articles', 'Galleries', 'Video', 'Text Block'];

const fontFace = (weight, style, file) =>
  `@font-face{font-family:"Roboto KB";font-style:${style};font-weight:${weight};font-display:swap;` +
  `src:url(${asset(`fonts/${file}`)}) format("woff2");}`;

// Self-hosted Roboto (latin), so the page has no dependency on Google Fonts
const FONT_CSS = [
  fontFace(400, 'normal', 'roboto-400-normal.woff2'),
  fontFace(500, 'normal', 'roboto-500-normal.woff2'),
  fontFace(700, 'normal', 'roboto-700-normal.woff2'),
  fontFace(400, 'italic', 'roboto-400-italic.woff2'),
  fontFace(700, 'italic', 'roboto-700-italic.woff2')
].join('');

const SearchBar = ({ query, onSearch }) => {
  const [value, setValue] = useState(query || '');
  useEffect(() => setValue(query || ''), [query]);

  const submit = (e) => {
    e.preventDefault();
    onSearch(value.trim());
  };

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto', textAlign: 'center' }}>
      <Box component="form" role="search" onSubmit={submit} sx={{ position: 'relative' }}>
        <Box
          component="input"
          type="search"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Search the Knowledge Base"
          aria-label="Search the Knowledge Base"
          autoComplete="off"
          sx={{
            display: 'block',
            width: '100%',
            height: 57,
            boxSizing: 'border-box',
            border: 0,
            borderRadius: '3px',
            outline: 'none',
            bgcolor: '#fff',
            color: KB_COLORS.accent,
            fontFamily: 'inherit',
            fontSize: 16,
            fontWeight: 700,
            py: 0,
            pl: '30px',
            pr: '90px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.12)',
            appearance: 'none',
            '&::placeholder': { color: '#7F8A93', fontStyle: 'italic', fontWeight: 400, opacity: 1 },
            '&::-webkit-search-cancel-button': { display: 'none' },
            '&:focus-visible': { boxShadow: `0 0 0 3px rgba(69,191,150,0.55)` }
          }}
        />
        <IconButton
          type="submit"
          aria-label="Search"
          sx={{ position: 'absolute', top: 0, right: 0, height: 57, width: 80, borderRadius: 0, color: KB_COLORS.accent }}
        >
          <SearchIcon />
        </IconButton>
      </Box>
      <Box
        sx={{
          display: 'inline-block',
          mt: '2px',
          px: 2.5,
          py: '5px',
          bgcolor: KB_COLORS.accent,
          color: '#fff',
          fontSize: 12,
          lineHeight: 1.5,
          borderRadius: '0 0 3px 3px'
        }}
      >
        Suggested Search:{' '}
        {SUGGESTED.map((term, i) => (
          <React.Fragment key={term}>
            <Box
              component={RouterLink}
              to={kbUrl.search(term)}
              sx={{ color: '#fff', textDecoration: 'underline', '&:hover': { textDecoration: 'none' } }}
            >
              {term}
            </Box>
            {i < SUGGESTED.length - 1 ? ', ' : ''}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
};

const HomeMenu = () => {
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState(null);
  const items = [{ label: 'Home', to: kbUrl.home() }, ...MENU_ITEMS];

  return (
    <>
      <Button
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-haspopup="true"
        aria-expanded={!!anchor}
        endIcon={<KeyboardArrowDownIcon sx={{ fontSize: '16px !important' }} />}
        sx={{
          color: '#fff',
          border: '1px solid',
          borderColor: anchor ? '#fff' : 'transparent',
          borderRadius: '3px',
          px: 1.5,
          py: 0.5,
          minWidth: 0,
          fontFamily: 'inherit',
          fontSize: 13,
          fontWeight: 500,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          '& .MuiButton-endIcon': { ml: 0.25 },
          '&:hover': { borderColor: '#fff', bgcolor: 'transparent' }
        }}
      >
        Home
      </Button>
      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { mt: 0.5, borderRadius: '3px', minWidth: 220 } } }}
      >
        {items.map((item) => (
          <MenuItem
            key={item.label}
            onClick={() => {
              setAnchor(null);
              navigate(item.to);
            }}
            sx={{ fontFamily: 'inherit', fontSize: 14, color: KB_COLORS.accentDark, py: 1 }}
          >
            {item.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

/**
 * Standalone layout for the public ePublisher Knowledge Base.
 * Has no links to any other part of the site, and asks search engines not to
 * index it - it is only reachable via its direct URL.
 *
 * `sidebar` (optional) is shown to the right of the content on desktop and
 * below it on mobile.
 */
const EPublisherLayout = ({ query = '', onSearch, sidebar = null, children }) => {
  // Keep this page out of search engine results (direct URL access only)
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        bgcolor: KB_COLORS.pageBg,
        color: KB_COLORS.text,
        fontFamily: FONT_STACK,
        fontSize: 16,
        '& *': { fontFamily: 'inherit' }
      }}
    >
      <GlobalStyles styles={FONT_CSS} />

      {/* Header */}
      <Box
        component="header"
        sx={{
          position: 'relative',
          color: '#fff',
          bgcolor: '#0A7DB4',
          backgroundImage: { xs: 'none', md: `url(${asset('theme/header.jpg')})` },
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
          minHeight: { xs: 0, md: 410 },
          pb: { xs: '84px', md: 0 },
          overflow: 'hidden',
          '&::before': { content: '""', position: 'absolute', inset: 0, zIndex: 1, bgcolor: 'rgba(0,0,0,0.1)', pointerEvents: 'none' }
        }}
      >
        {/* Small screens: the artwork is scaled so its "How can we help you today?" line stays centered above the search */}
        <Box
          aria-hidden="true"
          sx={{ display: { xs: 'block', md: 'none' }, position: 'absolute', inset: 0, overflow: 'hidden' }}
        >
          <Box
            component="img"
            src={asset('theme/header.jpg')}
            alt=""
            sx={{ position: 'absolute', top: 0, left: '50%', ml: '-664px', width: 1330, maxWidth: 'none', height: 'auto' }}
          />
        </Box>
        <Box sx={{ position: 'relative', zIndex: 2, maxWidth: 1300, mx: 'auto', px: { xs: 2, xl: 0 } }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', pt: '20px' }}>
            <Box
              component={RouterLink}
              to={KB_HOME_PATH}
              aria-label={`${SITE_TITLE} home`}
              sx={{ display: 'flex', alignItems: 'center', gap: '14px', color: '#fff', textDecoration: 'none', minWidth: 0 }}
            >
              <Box
                component="img"
                src={asset('theme/logo-symbol-white.png')}
                alt=""
                sx={{ height: { xs: 40, md: 54 }, width: 'auto', display: 'block', flexShrink: 0 }}
              />
              <Box sx={{ minWidth: 0 }}>
                <Typography component="span" sx={{ display: 'block', fontSize: { xs: 14, md: 16 }, fontWeight: 700, lineHeight: 1.3 }}>
                  {SITE_TITLE}
                </Typography>
                <Typography
                  component="span"
                  sx={{ display: { xs: 'none', md: 'block' }, fontSize: 12, fontStyle: 'italic', fontWeight: 500, lineHeight: 1.3 }}
                >
                  {SITE_TAGLINE}
                </Typography>
              </Box>
            </Box>
            <HomeMenu />
          </Box>

          <Box sx={{ mt: { xs: '76px', md: '110px' } }}>
            <SearchBar query={query} onSearch={onSearch} />
          </Box>
        </Box>
      </Box>

      {/* Content box */}
      <Box
        component="main"
        sx={{
          position: 'relative',
          zIndex: 3,
          width: { xs: 'calc(100% - 24px)', xl: '100%' },
          maxWidth: 1300,
          mx: 'auto',
          mt: { xs: '-40px', md: '-45px' }
        }}
      >
        <Box
          sx={{
            bgcolor: '#fff',
            display: 'grid',
            gridTemplateColumns: sidebar ? { xs: 'minmax(0, 1fr)', md: 'minmax(0, 872fr) minmax(0, 428fr)' } : 'minmax(0, 1fr)',
            minHeight: 320
          }}
        >
          <Box sx={{ minWidth: 0, p: { xs: '32px 20px 40px', md: '60px 60px 60px' } }}>{children}</Box>
          {sidebar && (
            <Box
              component="aside"
              sx={{
                minWidth: 0,
                borderTop: { xs: `1px solid ${KB_COLORS.line}`, md: 0 },
                borderLeft: { md: `1px solid ${KB_COLORS.line}` }
              }}
            >
              {sidebar}
            </Box>
          )}
        </Box>
      </Box>

      <Box sx={{ flexGrow: 1, minHeight: { xs: 40, md: 30 } }} />

      {/* Footer */}
      <Box component="footer" sx={{ bgcolor: KB_COLORS.footerBg, color: '#fff' }}>
        <Box sx={{ maxWidth: 1300, mx: 'auto', px: 2, pt: '48px', pb: '40px', textAlign: 'center' }}>
          <Box
            component="img"
            src={asset('theme/logo-footer.png')}
            alt="ePublisher"
            sx={{ display: 'block', mx: 'auto', width: 300, maxWidth: '80%', height: 'auto', mb: '28px' }}
          />
          <Typography sx={{ maxWidth: 1240, mx: 'auto', fontSize: 14, lineHeight: 1.7 }}>
            ePublisher is Australia’s leading communication platform for schools to create and send beautiful, engaging
            enewsletters, eSign digital forms, surveys, calendars &amp; messages.
          </Typography>
          <Box
            sx={{
              mt: '28px',
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'center',
              gap: { xs: 1, sm: '120px' },
              fontSize: 14
            }}
          >
            <Typography sx={{ fontSize: 'inherit' }}>No technical knowledge required.</Typography>
            <Typography sx={{ fontSize: 'inherit' }}>Available online 24/7 from home or office.</Typography>
          </Box>
        </Box>
        <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <Box sx={{ maxWidth: 1300, mx: 'auto', px: 2, height: 72, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
            <IconButton
              aria-label="Back to top"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              sx={{ color: KB_COLORS.accent }}
            >
              <KeyboardArrowUpIcon />
            </IconButton>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default EPublisherLayout;
